import { useState, useEffect, useRef, useCallback } from "react";
import { Typography } from "storybook/components/typography";
import { useTransifexLoader } from "context/transifex-loader";
import { VideoContainer, PromptContainer } from "./styles";

type Props = {
  src?: string;
  label?: string;
};

type Subtitles = {
  [key: string]: {
    label: string;
    default?: boolean;
    src: string;
    prompt?: string;
  };
};

export function Video({ src, label = "welcome video" }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [subtitleData, setSubtitleData] = useState<Subtitles | null>(null);
  const [videoSrc, setVideoSrc] = useState<string | undefined>(src);
  const BASE_URL = src?.substring(0, src.lastIndexOf("/"));
  const { lang } = useTransifexLoader();

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    (async () => {
      try {
        const response = await fetch(`${BASE_URL}/subtitles.json`);
        if (response.ok) {
          const data: Subtitles = await response.json();
          setSubtitleData(data);

          Object.entries(data).forEach(async ([language, subtitle]) => {
            const response = await fetch(`${BASE_URL}/${subtitle.src}`);
            const text = await response.text();

            const vttBlob = new Blob([text], { type: "text/vtt" });

            const track = document.createElement("track");
            track.default = subtitle.default || false;
            track.src = URL.createObjectURL(vttBlob);
            track.label = subtitle.label;
            track.kind = "subtitles";
            track.srclang = language;

            video.append(track);
          });
        }
      } catch (error) {
        console.error("Error fetching subtitle data", error);
      }
    })();
  }, [BASE_URL]);

  const restartVideoTimeout = useCallback(() => {
    const interval = setInterval(() => {
      if (videoRef.current && videoRef.current.paused) {
        videoRef.current.currentTime = 1;
      }
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  const handleTrackEvent = useCallback(
    (event) => {
      if (event.track.language === lang) {
        event.track.mode = "showing";
      } else {
        event.track.mode = "hidden";
      }
    },
    [lang]
  );

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      video.onerror = () => {
        console.error("Error loading video", video.src);
        // error loading localized video, so reset video src to original src which is english by default
        setVideoSrc(src);
      };

      const fileType = src?.split(".").pop();
      if (lang) {
        if (lang === "en") {
          // if language is english, set video src to original src which is english by default
          setVideoSrc(src);
        } else {
          const localizedVideoSrc = src?.replace(
            `.${fileType}`,
            `-${lang}.${fileType}`
          );
          setVideoSrc(localizedVideoSrc);
        }
      }

      for (const track of video.textTracks) {
        if (track.language === lang) {
          track.mode = "showing";
        } else {
          track.mode = "hidden";
        }
      }

      if (typeof video.textTracks.addEventListener === "function") {
        video.textTracks.addEventListener("addtrack", handleTrackEvent);
      }

      const clearRestartInterval = restartVideoTimeout();

      return () => {
        if (typeof video.textTracks.removeEventListener === "function") {
          video.textTracks.removeEventListener("addtrack", handleTrackEvent);
        }

        clearRestartInterval();
      };
    }
  }, [handleTrackEvent, lang, restartVideoTimeout, src]);

  if (!src) return null;

  return (
    <VideoContainer ref={videoContainerRef}>
      <video
        src={videoSrc}
        ref={videoRef}
        preload="true"
        aria-label={label}
        controls={true}
      ></video>
      {subtitleData && (
        <PromptContainer>
          {Object.values(subtitleData).map((subtitle) => (
            <Typography
              variant="body1"
              theme="default"
              color="ciqBrandDark"
              key={subtitle.label}
              component="p"
            >
              {subtitle.prompt}
            </Typography>
          ))}
        </PromptContainer>
      )}
    </VideoContainer>
  );
}
